import { useEffect, useState } from 'react';
import API from '../API';
import { useMarkdownRenderer } from '../hooks/useMarkdownRenderer';
import { useRunOnce } from '../hooks/useRunOnce';
import { useFileUpload } from './useFileUpload';
import { DiscoveryButton, DiscoveryIntent, Message } from './types';
import type { ChatData } from '../chat/ChatData';

/**
 * The real `ChatData`: message history, the backend-served prompt buttons,
 * sending turns to `/discovery_chat`, and (via useFileUpload) attachments.
 * What is typed into the box is the view's own state, not this hook's.
 */
export function useConversation(): ChatData {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  // Backend-served prompt buttons shown by the text box; clicking one seeds a
  // conversation in that direction (the chat is otherwise free-form).
  const [discoveryButtons, setDiscoveryButtons] = useState<DiscoveryButton[]>(
    []
  );
  const [initialMessagesLoading, setInitialMessagesLoading] = useState(true);

  const renderMarkdown = useMarkdownRenderer();
  const files = useFileUpload(messages, setMessages);

  // Initial conversation load (once on mount).
  useRunOnce(() => {
    void (async () => {
      setInitialMessagesLoading(true);
      try {
        const res = await API.post(`/get_discovery_conversation`, {});
        setMessages(res.data);
        setInitialMessagesLoading(false);

        console.log('Polling all messages on page load');
        files.pollFileStatuses(res.data);
      } catch (error) {
        setInitialMessagesLoading(false);
        console.error('Error fetching initial message:', error);
      }
    })();
  });

  // Fetch the prompt buttons (backend is the single source of truth for the
  // labels + intent values).
  useEffect(() => {
    const fetchButtons = async () => {
      try {
        const res = await API.post(`/get_discovery_buttons`, {});
        setDiscoveryButtons(res.data);
      } catch (error) {
        console.error('Error fetching discovery buttons:', error);
      }
    };
    fetchButtons();
  }, []);

  // Core send routine. A typed turn passes the text (and shows a user bubble);
  // a button click passes an empty message + an intent (no user bubble — it
  // just seeds Dooglie's next response, matching the backend, which only
  // enqueues a discovery entry when there is user text).
  const sendTurn = async (
    messageInput: string,
    intent?: DiscoveryIntent,
    prompt?: string
  ) => {
    const currentFileIds = files.fileIds;

    if (messageInput.trim() !== '' || currentFileIds.length > 0) {
      // Messages are stored newest first, so find the first 'ai' message to
      // grab its discovery_entry_id.
      setMessages((prevMessages) => {
        const mostRecentBotMessage = prevMessages.find(
          (msg) => msg.type === 'ai'
        );
        const botDiscoveryEntryId =
          mostRecentBotMessage?.discovery_entry_id || '';

        const userMessage: Message = {
          text: messageInput,
          type: 'user',
          discovery_entry_id: botDiscoveryEntryId,
          file_ids: currentFileIds.length > 0 ? currentFileIds : undefined,
          file_upload_status:
            currentFileIds.length > 0 ? 'IN_PROGRESS' : undefined,
        };

        return [userMessage, ...prevMessages];
      });
    }

    files.clearFiles(); // Clear the attachments (and any upload error) after sending

    setLoading(true);

    try {
      const res = await API.post(`/discovery_chat`, {
        user_message: messageInput,
        ...(intent ? { intent } : {}),
        ...(prompt ? { prompt } : {}),
      });

      const aiMessage: Message = {
        text: res.data.bot_message,
        type: 'ai',
        discovery_entry_id: res.data.discovery_entry_id,
      };

      setMessages((prevMessages) => [aiMessage, ...prevMessages]);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  // A typed turn needs text or an attachment; a button turn waits for the
  // reply in flight. Neither goes while a file is still uploading.
  const send = async (
    text: string,
    intent?: DiscoveryIntent,
    prompt?: string
  ) => {
    if (files.uploadingFiles) return;
    if (intent) {
      if (loading) return;
    } else if (text.trim() === '' && files.fileIds.length === 0) {
      return;
    }
    await sendTurn(text, intent, prompt);
  };

  return {
    messages,
    loading,
    initialLoading: initialMessagesLoading,
    buttons: discoveryButtons,
    send,
    upload: files,
    renderMarkdown,
  };
}
